/**
 * Claim recommendation endpoints (PRD-0381 D-10, D-11, D-14): the
 * recommendation queue and detail, the three decisions a person takes on a
 * row, and the execution policy. Every decision and the policy write carry a
 * required If-Match: the row's `metadata_version` for a decision, the
 * policy's `policy_version` for its write. The queue's repeatable filters ride
 * the same repeated-key serializer the claim queue uses.
 */

import type {
  ClaimExecutionPolicy,
  ClaimExecutionPolicyUpdate,
  ClaimRecommendation,
  ClaimRecommendationListFilters,
  ClaimRecommendationListResponse,
  RecommendationDecisionResponse,
} from '@/types/smem'
import { apiClient } from './client'
import { ifMatchHeaders } from './ifMatch'
import { repeatedKeyParamsSerializer } from './queryParams'

const CLAIMS_PATH = '/api/v1/semantic-memory/claims'
const RECOMMENDATIONS_PATH = `${CLAIMS_PATH}/recommendations`
const POLICY_PATH = `${CLAIMS_PATH}/execution-policy/`

export const claimRecommendations = {
  list: async (
    filters?: ClaimRecommendationListFilters
  ): Promise<ClaimRecommendationListResponse> => {
    const response = await apiClient.get<ClaimRecommendationListResponse>(
      `${RECOMMENDATIONS_PATH}/`,
      { params: filters, paramsSerializer: repeatedKeyParamsSerializer }
    )
    return response.data
  },

  get: async (id: string): Promise<ClaimRecommendation> => {
    const response = await apiClient.get<ClaimRecommendation>(
      `${RECOMMENDATIONS_PATH}/${id}/`
    )
    return response.data
  },

  /** The reason is optional on an approval; an absent one sends an empty body. */
  approve: async (
    id: string,
    ifMatch: number,
    reason?: string
  ): Promise<RecommendationDecisionResponse> => {
    const response = await apiClient.post<RecommendationDecisionResponse>(
      `${RECOMMENDATIONS_PATH}/${id}/approve/`,
      reason === undefined ? {} : { reason },
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  dismiss: async (
    id: string,
    ifMatch: number,
    reason: string
  ): Promise<RecommendationDecisionResponse> => {
    const response = await apiClient.post<RecommendationDecisionResponse>(
      `${RECOMMENDATIONS_PATH}/${id}/dismiss/`,
      { reason },
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  /** A requeue sends the referral back to the lane and carries no body. */
  requeue: async (id: string, ifMatch: number): Promise<RecommendationDecisionResponse> => {
    const response = await apiClient.post<RecommendationDecisionResponse>(
      `${RECOMMENDATIONS_PATH}/${id}/requeue/`,
      undefined,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  policy: {
    get: async (): Promise<ClaimExecutionPolicy> => {
      const response = await apiClient.get<ClaimExecutionPolicy>(POLICY_PATH)
      return response.data
    },

    update: async (
      payload: ClaimExecutionPolicyUpdate,
      ifMatch: number
    ): Promise<ClaimExecutionPolicy> => {
      const response = await apiClient.put<ClaimExecutionPolicy>(
        POLICY_PATH,
        payload,
        ifMatchHeaders(ifMatch)
      )
      return response.data
    },
  },
}
